import React, { useState, useCallback, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import * as Yup from 'yup';
import PropTypes from 'prop-types';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import { useNotification } from '../../components/Notification/NotificationContext';
import Forms from '../../components/Forms';
import ErrorProcessor from '../../components/ErrorProcessor';

export default function CustomerPay({ customer }) {
  const routeName = '/customerspayments';
  const dataName = 'Cliente';
  const [apiErrors, setApiErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const notification = useNotification();
  const [customerEdit, setCustomer] = useState(customer);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [banks, setBanks] = useState([]);

  const [formEditPay, setFormEditPay] = useState({
    bank_code: '',
    bank_branch: '',
    bank_account: '',
    bank_account_digit: '',
    bank_pix: '',
    bank_account_type: '',
    account_owner: '',
    previous_benefit_amount: 0,
    category: '',
    dib: '',
    beneficiary_number: '',
    customer_id: customerEdit.id,
  });

  const validationSchemaPay = Yup.object().shape({
    bank_code: Yup.number()
      .typeError('Deve ser numérico')
      .required('Banco obrigatório.'),
    bank_branch: Yup.number()
      .typeError('Deve ser numérico')
      .required('Agência obrigatória.'),
    bank_account: Yup.number()
      .typeError('Deve ser numérico')
      .required('Conta obrigatória.'),
    bank_account_digit: Yup.number()
      .typeError('Deve ser numérico')
      .required('Dígito da conta obrigatória.'),
    account_owner: Yup.string().typeError('Deve ser um texto'),
    bank_account_type: Yup.string().typeError('Deve ser um texto'),
    bank_pix: Yup.string()
      .typeError('Deve ser um texto')
      .min(7, 'Chave Pix deve conter no mínimo 7 caracteres'),
    previous_benefit_amount: Yup.number().typeError('Deve ser numérico'),
    beneficiary_number: Yup.number().typeError('Deve ser numérico'),
  });

  const handleFieldChangePay = useCallback((fieldName, value) => {
    setFormEditPay((prevValues) => ({ ...prevValues, [fieldName]: value }));
  }, []);

  const fieldsPay = [
    {
      name: 'bank_code',
      id: 'bank_code',
      label: 'Código banco',
      type: 'select',
      options: banks,
      onChange: (e) => handleFieldChangePay('bank_code', e.target.value),
      colSize: 3,
      additionalProps: {},
      group: 'Dados bancários',
    },
    {
      name: 'bank_branch',
      id: 'bank_branch',
      label: 'Agência',
      type: 'number',
      onChange: (e) => handleFieldChangePay('bank_branch', e.target.value),
      colSize: 2,
      additionalProps: {},
      group: 'Dados bancários',
    },
    {
      name: 'bank_account',
      id: 'bank_account',
      label: 'Conta',
      type: 'number',
      onChange: (e) => handleFieldChangePay('bank_account', e.target.value),
      colSize: 2,
      additionalProps: {},
      group: 'Dados bancários',
    },
    {
      name: 'bank_account_digit',
      id: 'bank_account_digit',
      label: 'Dígito',
      type: 'number',
      onChange: (e) =>
        handleFieldChangePay('bank_account_digit', e.target.value),
      colSize: 2,
      additionalProps: {},
      group: 'Dados bancários',
    },
    {
      name: 'bank_account_type',
      id: 'bank_account_type',
      label: 'Tipo de recebimento',
      type: 'text',
      onChange: (e) =>
        handleFieldChangePay('bank_account_type', e.target.value),
      colSize: 3,
      additionalProps: {},
      group: 'Dados bancários',
    },
    {
      name: 'account_owner',
      id: 'account_owner',
      label: 'Titular da conta',
      type: 'text',
      onChange: (e) => handleFieldChangePay('account_owner', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Dados bancários',
    },
    {
      name: 'bank_pix',
      id: 'bank_pix',
      label: 'Chave Pix',
      type: 'text',
      onChange: (e) => handleFieldChangePay('bank_pix', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Dados bancários',
    },
    {
      name: 'beneficiary_number',
      id: 'beneficiary_number',
      label: 'Número do beneficiário',
      type: 'number',
      onChange: (e) =>
        handleFieldChangePay('beneficiary_number', e.target.value),
      colSize: 3,
      additionalProps: {},
      group: 'Dados bancários',
    },
    {
      name: 'previous_benefit_amount',
      id: 'previous_benefit_amount',
      label: 'Valor benefício anterior',
      type: 'number',
      onChange: (e) =>
        handleFieldChangePay('previous_benefit_amount', e.target.value),
      colSize: 3,
      additionalProps: {},
      group: 'Dados bancários',
    },
    {
      name: 'category',
      id: 'category',
      label: 'Espécie',
      type: 'text',
      onChange: (e) => handleFieldChangePay('category', e.target.value),
      colSize: 3,
      additionalProps: {},
      group: 'Dados bancários',
    },
    {
      name: 'dib',
      id: 'dib',
      label: 'DIB',
      type: 'text',
      onChange: (e) => handleFieldChangePay('dib', e.target.value),
      colSize: 3,
      additionalProps: {},
      group: 'Dados bancários',
    },
  ];

  const getCustomer = useCallback(async () => {
    try {
      const request = await axios.get(`customers/${customerEdit.id}`);

      if (request.data.CustomerPayments.length > 0) {
        setFormEditPay({ ...request.data.CustomerPayments[0] });
      }
      setCustomer(request.data);
    } catch (err) {
      setApiErrors(err);
    }
  }, [customerEdit]);

  const getBanks = useCallback(async () => {
    try {
      const optionsBanks = [{ value: '', label: '-- Selecione --' }];
      const request = await axios.get(`banks`);

      if (request) {
        request.data.forEach((data) => {
          optionsBanks.push({
            value: data.code,
            label: `${data.code} | ${data.name}`,
          });
        });
      }

      setBanks(optionsBanks);
    } catch (err) {
      setApiErrors(err);
    }
  }, []);

  const handleSubmitPay = useCallback(async () => {
    try {
      setIsLoading(true);

      const selectedBank = banks.find(
        (bank) => bank.value === formEditPay.bank_code
      );

      const bankName = selectedBank ? selectedBank.label.split(' | ')[1] : '';

      const updatedFormEditPay = {
        ...formEditPay,
        bank_name: bankName,
      };

      let action = 'atualizados';
      if (formEditPay.id) {
        await axios.put(`${routeName}/${formEditPay.id}`, updatedFormEditPay);
      } else {
        await axios.post(routeName, updatedFormEditPay);
        action = 'cadastrados';
      }

      notification(`Dados bancários ${action} com sucesso`, 'success');
      await getCustomer();
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  }, [banks, getCustomer, formEditPay, notification]);

  useEffect(() => {
    if (customerEdit.CustomerPayments.length > 0) {
      setFormEditPay({ ...customerEdit.CustomerPayments[0] });
    }
    getBanks();
    setDataLoaded(true);
  }, []);

  return (
    <Card className="mb-5">
      <Loading isLoading={isLoading} />
      <ErrorProcessor error={apiErrors} />
      <Card.Header>
        <h4 className="card-heading">
          Atualizar dados bancários do {dataName}
        </h4>
      </Card.Header>
      <Card.Body>
        {dataLoaded ? (
          <Forms
            initialValues={formEditPay}
            validationSchema={validationSchemaPay}
            onSubmit={handleSubmitPay}
            fields={fieldsPay}
            btnSubmit={{
              variant: 'primary',
              type: 'submit',
              text: 'Atualizar',
            }}
          />
        ) : null}
      </Card.Body>
    </Card>
  );
}

CustomerPay.propTypes = {
  customer: PropTypes.shape({}).isRequired,
};
